import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { 
  FaTimes, FaCalendarAlt, FaMapMarkerAlt, FaClock, FaUsers, 
  FaPlay, FaEdit, FaExternalLinkAlt, FaTrophy, FaChartBar,
  FaCheckCircle, FaTimesCircle, FaQuestionCircle
} from 'react-icons/fa';
import RSVPButtons from './RSVPButtons';
import { useRSVP } from '../hooks/useRSVP';
import StartEventModal from './Scorecard/StartEventModal';
import LiveScorecardModal from './Scorecard/LiveScorecardModal';
import FinalResultModal from './Scorecard/FinalResultModal';
import ReadOnlyScorecard from './Scorecard/ReadOnlyScorecard';
import styles from './EventDetailsModal.module.css';

const EventDetailsModal = ({ 
  event, 
  onClose, 
  weatherData = {},
  canManage = false,
  programId,
  programDetails,
  onEditEvent,
  onEventUpdated
}) => {
  const rsvpHook = useRSVP(event._id, event);
  
  // Modal states for nested modals
  const [showStartEventModal, setShowStartEventModal] = useState(false);
  const [showLiveScorecardModal, setShowLiveScorecardModal] = useState(false);
  const [showFinalResultModal, setShowFinalResultModal] = useState(false);
  const [showScoreViewer, setShowScoreViewer] = useState(false);

  const { location } = event;
  const coordinatesArray = location?.address?.coordinates?.coordinates;
  const hasValidCoordinates = Array.isArray(coordinatesArray) && coordinatesArray.length >= 2;
  const lat = hasValidCoordinates ? coordinatesArray[1] : null;
  const lng = hasValidCoordinates ? coordinatesArray[0] : null;
  const isValidLatLng = typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng);
  const weather = weatherData[event._id];

  // Format date/time
  const eventDate = new Date(event.dateTime.start);
  const eventEndDate = event.dateTime.end ? new Date(event.dateTime.end) : null;
  
  const formattedDate = eventDate.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric',
    month: 'long', 
    day: 'numeric' 
  });
  const formattedStartTime = eventDate.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit' 
  });
  const formattedEndTime = eventEndDate?.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit' 
  });

  // Get team names
  const homeTeam = event.teams?.home?.name || 'Home';
  const awayTeam = event.teams?.away?.name || event.opponentTeam || 'Away';
  const isPractice = event.type === 'Practice';

  // Score info
  const hasLiveScore = event.status === 'Live' && event.liveSession?.scorecard;
  const hasFinalScore = event.status === 'Completed' && event.finalResults;
  const homeScore = hasFinalScore ? event.finalResults.homeScore : (hasLiveScore ? event.liveSession.scorecard.score.home : null);
  const awayScore = hasFinalScore ? event.finalResults.awayScore : (hasLiveScore ? event.liveSession.scorecard.score.away : null);

  // Status color mapping
  const getStatusClass = (status) => {
    const statusMap = {
      'Live': styles.statusLive,
      'Active': styles.statusActive,
      'Planned': styles.statusPlanned,
      'Completed': styles.statusCompleted,
      'Cancelled': styles.statusCancelled,
      'Postponed': styles.statusPostponed
    };
    return statusMap[status] || styles.statusPlanned;
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Header with Status */}
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <span className={`${styles.statusBadge} ${getStatusClass(event.status)}`}>
              {event.status}
              {event.status === 'Live' && <span className={styles.liveDot} />}
            </span>
            <span className={styles.eventType}>{event.type}</span>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {/* Hero Section with Score/Matchup */}
          <div className={styles.heroSection}>
            {/* Event Title - Always shown */}
            <h1 className={styles.eventTitle}>{event.title}</h1>

            {isPractice ? (
              <div className={styles.practiceHero}>
                <p className={styles.practiceTeam}>{homeTeam} • {event.type}</p>
              </div>
            ) : (
              <div className={styles.matchupHero}>
                <div className={styles.teamBlock}>
                  <span className={styles.teamLabel}>HOME</span>
                  <h2 className={styles.teamName}>{homeTeam}</h2>
                </div>
                
                <div className={styles.scoreBlock}>
                  {(hasLiveScore || hasFinalScore) ? (
                    <>
                      <div className={styles.scoreDisplay}>
                        <span className={styles.score}>{homeScore}</span>
                        <span className={styles.scoreDivider}>-</span>
                        <span className={styles.score}>{awayScore}</span>
                      </div>
                      {hasFinalScore && event.finalResults.winner && (
                        <span className={styles.resultBadge}>
                          {event.finalResults.winner === 'tie' ? 'Draw' : 
                           event.finalResults.winner === 'home' ? 'Home Win' : 'Away Win'}
                        </span>
                      )}
                    </>
                  ) : (
                    <div className={styles.vsDisplay}>VS</div>
                  )}
                </div>
                
                <div className={styles.teamBlock}>
                  <span className={styles.teamLabel}>AWAY</span>
                  <h2 className={styles.teamName}>{awayTeam}</h2>
                </div>
              </div>
            )}
          </div>

          {/* Event Info Grid */}
          <div className={styles.infoGrid}>
            {/* Date & Time */}
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>
                <FaCalendarAlt />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Date & Time</span>
                <span className={styles.infoValue}>{formattedDate}</span>
                <span className={styles.infoSubvalue}>
                  <FaClock /> {formattedStartTime}
                  {formattedEndTime && ` - ${formattedEndTime}`}
                </span>
              </div>
            </div>

            {/* Location */}
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>
                <FaMapMarkerAlt />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Location</span>
                <span className={styles.infoValue}>{location?.name || 'TBD'}</span>
                {location?.address && (
                  <span className={styles.infoSubvalue}>
                    {[location.address.street, location.address.city, location.address.state]
                      .filter(val => typeof val === 'string' && val)
                      .join(', ')}
                  </span>
                )}
                {isValidLatLng && (
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.mapLink}
                  >
                    <FaExternalLinkAlt /> Open in Maps
                  </a>
                )}
              </div>
            </div>

            {/* Weather (if available) */}
            {weather && (
              <div className={styles.infoCard}>
                <div className={styles.infoIcon}>
                  <img 
                    src={`https://openweathermap.org/img/wn/${weather.weather[0].icon}@2x.png`}
                    alt={weather.weather[0].description}
                    className={styles.weatherIcon}
                  />
                </div>
                <div className={styles.infoContent}>
                  <span className={styles.infoLabel}>Weather</span>
                  <span className={styles.infoValue}>{Math.round(weather.main.temp)}°C</span>
                  <span className={styles.infoSubvalue}>{weather.weather[0].description}</span>
                </div>
              </div>
            )}

            {/* RSVP Summary */}
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>
                <FaUsers />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Attendance</span>
                <div className={styles.rsvpSummary}>
                  <span className={styles.rsvpGoing}>
                    <FaCheckCircle /> {rsvpHook.summary?.going || 0} Going
                  </span>
                  <span className={styles.rsvpNotGoing}>
                    <FaTimesCircle /> {rsvpHook.summary?.notGoing || 0} Not Going
                  </span>
                  <span className={styles.rsvpMaybe}>
                    <FaQuestionCircle /> {rsvpHook.summary?.maybe || 0} Maybe
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Map Preview */}
          {isValidLatLng && (
            <div className={styles.mapSection}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.mapPreview}
              >
                <img
                  src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=14&size=600x200&markers=color:red%7C${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&style=feature:all%7Celement:labels%7Cvisibility:simplified&style=feature:road%7Celement:geometry%7Ccolor:0x444444&style=feature:landscape%7Celement:geometry%7Ccolor:0x1a1a1a&style=feature:poi%7Cvisibility:off&style=feature:water%7Celement:geometry%7Ccolor:0x0d0d0d`}
                  alt={`Map showing ${location?.name || 'event location'}`}
                  className={styles.mapImage}
                />
                <div className={styles.mapOverlay}>
                  <FaExternalLinkAlt /> View in Google Maps
                </div>
              </a>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className={styles.descriptionSection}>
              <h3 className={styles.sectionTitle}>Description</h3>
              <p className={styles.description}>{event.description}</p>
            </div>
          )}

          {/* RSVP Section - Only for non-completed events */}
          {event.status !== 'Completed' && event.status !== 'Cancelled' && (
            <div className={styles.rsvpSection}>
              <h3 className={styles.sectionTitle}>Your RSVP</h3>
              <RSVPButtons
                eventId={event._id}
                event={event}
                userRSVP={rsvpHook.userRSVP}
                summary={rsvpHook.summary}
                onRSVPUpdate={rsvpHook.refresh}
                canRSVP={rsvpHook.canRSVP}
                submitting={rsvpHook.submitting}
                error={rsvpHook.error}
                onSubmit={(response, note) => rsvpHook.submitRSVP(response, note)}
                onClearError={rsvpHook.clearError}
              />
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className={styles.modalFooter}>
          {/* View Score/Results buttons */}
          {event.status === 'Live' && hasLiveScore && (
            <button 
              onClick={() => setShowScoreViewer(true)} 
              className={styles.actionButton}
            >
              <FaChartBar /> Live Score
            </button>
          )}
          
          {event.status === 'Completed' && hasFinalScore && (
            <button 
              onClick={() => setShowFinalResultModal(true)} 
              className={styles.actionButton}
            >
              <FaTrophy /> View Results
            </button>
          )}

          {/* Manager Actions */}
          {canManage && (
            <>
              {(event.status === 'Planned' || event.status === 'Active') && programId && programDetails && (
                <button 
                  onClick={() => setShowStartEventModal(true)} 
                  className={styles.primaryButton}
                >
                  <FaPlay /> Start Event
                </button>
              )}
              
              {event.status === 'Live' && (
                <button 
                  onClick={() => setShowLiveScorecardModal(true)} 
                  className={styles.primaryButton}
                >
                  🎯 Manage Scorecard
                </button>
              )}

              {onEditEvent && (
                <button 
                  onClick={() => { onClose(); onEditEvent(event); }} 
                  className={styles.secondaryButton}
                >
                  <FaEdit /> Edit Event
                </button>
              )}
            </>
          )}

          <button onClick={onClose} className={styles.closeBtn}>
            Close
          </button>
        </div>
      </div>

      {/* Nested Modals */}
      {showStartEventModal && programId && programDetails && (
        <StartEventModal
          event={event}
          programScorecard={programDetails.scorecard}
          onClose={() => setShowStartEventModal(false)}
          onEventStarted={(updatedEvent) => {
            setShowStartEventModal(false);
            setShowLiveScorecardModal(true);
            onEventUpdated?.(updatedEvent);
          }}
        />
      )}

      {showLiveScorecardModal && event.status === 'Live' && (
        <LiveScorecardModal
          event={event}
          onClose={() => setShowLiveScorecardModal(false)}
          programSport={programDetails?.sport}
          onEventUpdated={onEventUpdated}
        />
      )}

      {showScoreViewer && event.status === 'Live' && (
        <ReadOnlyScorecard
          event={event}
          onClose={() => setShowScoreViewer(false)}
          onRefresh={onEventUpdated}
        />
      )}

      {showFinalResultModal && event.status === 'Completed' && (
        <FinalResultModal
          event={event}
          onClose={() => setShowFinalResultModal(false)}
        />
      )}
    </div>,
    document.body
  );
};

export default EventDetailsModal;

